import { setAppsClientId, warmSettingsCache } from "@apps/client-sdk";
import { mountEmailAppsApp } from "@apps/client-ui";
import { createPwaSettingsCache, createPwaStorage } from "./storage.js";

setAppsClientId("pwa");

declare const __APP_VERSION__: string;

const SETTINGS_WARM_INTERVAL_MS = 15 * 60 * 1000;

function cleanTransientUrlParams(): void {
  const params = new URLSearchParams(window.location.search);
  let changed = false;
  for (const key of ["updated", "pair"]) {
    if (params.has(key)) {
      params.delete(key);
      changed = true;
    }
  }
  if (!changed) {
    return;
  }
  const remainder = params.toString();
  const nextUrl =
    window.location.pathname +
    (remainder ? `?${remainder}` : "") +
    window.location.hash;
  window.history.replaceState({}, "", nextUrl);
}

function readPairingCodeFromUrl(): string | undefined {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("pair");
  const code = raw?.trim().toUpperCase();
  if (!code || code.length !== 6) {
    return undefined;
  }
  return code;
}

const initialPairingCode = readPairingCodeFromUrl();
cleanTransientUrlParams();

const root = document.getElementById("app");
if (!root) {
  throw new Error("Missing #app root element");
}

const storage = createPwaStorage();
const settingsCache = createPwaSettingsCache();

mountEmailAppsApp({
  storage,
  settingsCache,
  installedVersion: __APP_VERSION__,
  initialPairingCode,
  reloadOnUpdate: true,
  root,
  layout: "full",
  openUrl: (url) => {
    // window.open fails in iOS/Android standalone PWAs; anchor opens the system browser.
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.append(link);
    link.click();
    link.remove();
  },
});

// Shared init already warms on open; keep a long-lived tab fresh without blocking paint.
let lastSettingsWarmAt = Date.now();

function softWarmSettingsCache(): void {
  const now = Date.now();
  if (now - lastSettingsWarmAt < SETTINGS_WARM_INTERVAL_MS) {
    return;
  }
  lastSettingsWarmAt = now;
  void warmSettingsCache({ storage, settingsCache });
}

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") {
    softWarmSettingsCache();
  }
});

const requestIdle =
  typeof window.requestIdleCallback === "function"
    ? window.requestIdleCallback.bind(window)
    : (cb: () => void) => window.setTimeout(cb, 2000);
requestIdle(() => {
  softWarmSettingsCache();
});

if ("serviceWorker" in navigator) {
  void navigator.serviceWorker.register("./sw.js", { scope: "./" }).then((registration) => {
    void registration.update();
  });
}
